import styled, { keyframes } from 'styled-components'
import variables from '../../styles/variables'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

export const Container = styled.div`
  background: ${variables.colors.white};
  border: 2px solid ${variables.colors.greyLight};
  border-radius: 16px;
  padding: 2rem;
  margin: 2rem 0;
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 12px 35px rgba(0,0,0,0.12);
  }
`

export const Header = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`

export const Title = styled.h3`
  color: ${variables.colors.greyDarkest};
  font-family: ${variables.fonts.header};
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
`

export const Description = styled.p`
  color: ${variables.colors.greyDark};
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 0;
`

export const QuerySection = styled.div`
  margin-bottom: 2rem;
`

export const QueryTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`

export const QueryTab = styled.button`
  background: ${props => props.active ? variables.colors.primary : variables.colors.greyLightest};
  color: ${props => props.active ? variables.colors.white : variables.colors.greyDark};
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? variables.colors.primary : variables.colors.greyLight};
    transform: translateY(-2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 166, 147, 0.2);
  }
`

export const QueryDisplay = styled.div`
  background: ${variables.colors.greyLightest};
  border-radius: 12px;
  padding: 1.5rem;
`

export const QueryTitle = styled.h4`
  color: ${variables.colors.greyDarkest};
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`

export const QueryDescription = styled.p`
  color: ${variables.colors.greyDark};
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.5;
`

export const CodeBlock = styled.div`
  background: ${variables.colors.greyLightest};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
`

export const CodeHeader = styled.div`
  background: ${variables.colors.greyLight};
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${variables.colors.grey};
`

export const CodeLanguage = styled.span`
  color: ${variables.colors.greyDark};
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const RunButton = styled.button`
  background: ${variables.colors.primary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: ${variables.colors.oracle.base};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 166, 147, 0.3);
  }
`

export const Code = styled.pre`
  color: ${variables.colors.greyDarkest};
  background: ${variables.colors.greyLightest};
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  padding: 1rem;
  margin: 0;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-weight: 500;
`

export const LoadingSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: ${fadeIn} 0.3s ease;
`

export const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${variables.colors.greyLight};
  border-top: 3px solid ${variables.colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-right: 1rem;
`

export const LoadingText = styled.span`
  color: ${variables.colors.greyDark};
  font-size: 1rem;
`

export const ErrorSection = styled.div`
  background: #fed7d7;
  border: 1px solid #fc8181;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  animation: ${fadeIn} 0.3s ease;
`

export const ErrorText = styled.p`
  color: #c53030;
  margin: 0;
  font-size: 0.9rem;
`

export const ResultsSection = styled.div`
  animation: ${fadeIn} 0.5s ease;
`

export const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${variables.colors.greyLight};
`

export const ResultsTitle = styled.h4`
  color: ${variables.colors.greyDarkest};
  font-size: 1.3rem;
  margin: 0;
  font-weight: 600;
`

export const ResultsCount = styled.span`
  color: ${variables.colors.greyDark};
  font-size: 0.9rem;
  background: ${variables.colors.greyLightest};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
`

export const TimingDisplay = styled.div`
  color: ${variables.colors.greyDark};
  font-size: 0.85rem;
  font-family: 'Courier New', monospace;
  background: ${variables.colors.greyLightest};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border-left: 3px solid ${variables.colors.oracle.medium};
`

export const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const ResultItem = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props.rank === 1 ? variables.colors.oracle.lightest : variables.colors.greyLightest};
  border: ${props => props.rank === 1 ? `2px solid ${variables.colors.primary}` : '1px solid transparent'};
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  }
`

export const ResultRank = styled.div`
  background: ${props => props.rank === 1 ? variables.colors.primary : variables.colors.greyDark};
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  margin-right: 1rem;
  flex-shrink: 0;
`

export const ResultContent = styled.div`
  flex-grow: 1;
`

export const ResultValue = styled.div`
  color: ${variables.colors.greyDarkest};
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`

export const ResultDetails = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`

export const ResultDetail = styled.span`
  color: ${variables.colors.greyDark};
  font-size: 0.85rem;
  background: ${variables.colors.white};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`

export const ProbabilityBadge = styled.div`
  background: ${props => {
    const confidence = props.confidence
    if (confidence > 0.7) return '#48bb78'
    if (confidence > 0.4) return '#ed8936'
    return '#e53e3e'
  }};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-left: 1rem;
  flex-shrink: 0;
`